import { useRef, useEffect, useState, useMemo } from "react";
import Globe from "react-globe.gl";
import { Reveal, Overline } from "./Reveal";
import { MapPin } from "lucide-react";
import { ORIGIN, DESTINATIONS, COUNTRIES } from "../../lib/content";
import { useLang } from "../../lib/i18n";

const GOLD = "#c9a24b";
const GROUPS = ["Italia", "Nel Mondo"];

export const GlobeSection = () => {
  const globeEl = useRef(null);
  const wrapRef = useRef(null);
  const { tr } = useLang();
  const [size, setSize] = useState(0);
  const [ready, setReady] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      setSize(Math.min(w, 600));
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!ready || !globeEl.current) return;
    const g = globeEl.current;
    const controls = g.controls();
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.6;
    controls.enableZoom = false;
    g.pointOfView({ lat: 40, lng: 14, altitude: 2.4 }, 0);
  }, [ready]);

  const arcs = useMemo(
    () =>
      DESTINATIONS.map((d) => ({
        startLat: ORIGIN.lat,
        startLng: ORIGIN.lng,
        endLat: d.lat,
        endLng: d.lng,
      })),
    []
  );

  const points = useMemo(
    () => DESTINATIONS.map((d) => ({ ...d, origin: false })),
    []
  );

  const focusPlace = (c) => {
    if (!globeEl.current) return;
    globeEl.current.controls().autoRotate = false;
    globeEl.current.pointOfView({ lat: c.lat, lng: c.lng, altitude: 2.0 }, 1200);
  };

  const handlePointClick = (p) => {
    setSelected(p);
    if (!globeEl.current) return;
    globeEl.current.controls().autoRotate = false;
    globeEl.current.pointOfView({ lat: p.lat, lng: p.lng, altitude: 2.0 }, 1200);
  };

  const pointLabelHtml = (p) =>
    `<div style="font-family:Manrope,sans-serif;font-size:11px;letter-spacing:0.05em;color:#050505;background:#c9a24b;padding:4px 10px;border-radius:999px;white-space:nowrap;box-shadow:0 4px 14px rgba(0,0,0,0.4);font-weight:600;">${p.name}</div>`;

  return (
    <section
      id="nel-mondo"
      data-testid="globe-section"
      className="relative bg-ad-black py-24 md:py-40 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text + countries */}
          <div>
            <Reveal>
              <Overline>{tr("Dal nostro Atelier al mondo", "From our Atelier to the world")}</Overline>
              <h2 className="mt-6 font-serif-display font-light text-4xl md:text-6xl leading-[1.02] tracking-tight text-white">
                {tr("Dove viaggiano", "Where our gowns")}{" "}
                <span className="italic text-gold">{tr("i nostri abiti", "travel")}</span>
              </h2>
              <p className="mt-6 font-sans-ui font-light text-white/65 leading-relaxed max-w-md">
                {tr(
                  "Ogni abito esce dal nostro negozio di Comiso e parte per un viaggio unico. Dalla Sicilia all'Italia, fino all'estero: le nostre spose e i nostri sposi hanno celebrato il loro giorno in tutto il mondo.",
                  "Every gown leaves our store in Comiso and sets off on a unique journey. From Sicily to mainland Italy and abroad: our brides and grooms have celebrated their day all over the world."
                )}
              </p>
              <div className="hairline w-24 mt-8" />
            </Reveal>

            <div className="mt-10 space-y-7">
              {GROUPS.map((group, gi) => {
                const list = COUNTRIES.filter((c) => c.group === group);
                return (
                  <Reveal key={group} delay={gi * 0.1} data-testid={`group-${group}`}>
                    <div className="flex items-baseline gap-3 mb-3">
                      <span className="font-sans-ui text-[11px] uppercase tracking-[0.28em] text-gold">
                        {group === "Italia" ? tr("Italia", "Italy") : tr("Nel Mondo", "Worldwide")}
                      </span>
                      <span className="hairline flex-1" />
                      <span className="font-serif-display text-sm text-white/40 tabular-nums">
                        {list.length}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {list.map((c) => (
                        <button
                          key={c.name}
                          data-testid={`globe-country-${c.name}`}
                          onClick={() => focusPlace(c)}
                          className="font-sans-ui text-xs text-white/70 border border-white/12 px-3 py-1.5 hover:border-gold hover:text-gold transition-colors duration-300"
                        >
                          {tr(c.name, c.nameEn)}
                        </button>
                      ))}
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>

          {/* Globe */}
          <Reveal delay={0.15} className="order-first lg:order-last">
            <div
              ref={wrapRef}
              className="relative w-full aspect-square max-w-[600px] mx-auto"
            >
              <div className="pointer-events-none absolute inset-0 rounded-full bg-gold/10 blur-3xl scale-90" />
              {size > 0 && (
                <Globe
                  ref={globeEl}
                  width={size}
                  height={size}
                  onGlobeReady={() => setReady(true)}
                  backgroundColor="rgba(0,0,0,0)"
                  globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
                  bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
                  atmosphereColor={GOLD}
                  atmosphereAltitude={0.16}
                  arcsData={arcs}
                  arcColor={() => [`rgba(201,162,75,0.12)`, `rgba(230,211,163,0.9)`]}
                  arcAltitudeAutoScale={0.32}
                  arcStroke={0.4}
                  arcDashLength={0.45}
                  arcDashGap={0.25}
                  arcDashAnimateTime={2600}
                  pointsData={points}
                  pointLat="lat"
                  pointLng="lng"
                  pointColor={() => GOLD}
                  pointAltitude={0.012}
                  pointRadius={0.22}
                  pointResolution={6}
                  pointLabel={pointLabelHtml}
                  onPointClick={handlePointClick}
                  labelsData={[{ ...ORIGIN }]}
                  labelLat="lat"
                  labelLng="lng"
                  labelText={(d) => d.name}
                  labelSize={0.75}
                  labelDotRadius={0.4}
                  labelColor={() => "#ffffff"}
                  labelResolution={2}
                />
              )}
            </div>
            <div className="mt-5 h-6 flex items-center justify-center">
              {selected ? (
                <span
                  data-testid="globe-selected-city"
                  className="inline-flex items-center gap-2 font-sans-ui text-xs uppercase tracking-[0.25em] text-gold"
                >
                  <MapPin size={13} /> {selected.name}
                </span>
              ) : (
                <span className="font-sans-ui text-[11px] uppercase tracking-[0.25em] text-white/40">
                  {tr("Trascina per ruotare · tocca un puntino per scoprirlo", "Drag to rotate · tap a dot to discover it")}
                </span>
              )}
            </div>
          </Reveal>
        </div>

        {/* Journey stats */}
        <Reveal delay={0.1}>
          <div
            data-testid="globe-stats"
            className="mt-16 md:mt-24 border-t border-white/12 pt-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
          >
            {[
              { value: "4", label: tr("Continenti", "Continents") },
              { value: String(COUNTRIES.length), label: tr("Nazioni", "Countries") },
              { value: `${DESTINATIONS.length}+`, label: tr("Città", "Cities") },
              { value: "∞", label: tr("Emozioni", "Emotions") },
            ].map((s) => (
              <div key={s.label}>
                <span className="block font-serif-display font-light text-5xl md:text-6xl text-white leading-none">
                  {s.value}
                </span>
                <span className="mt-3 inline-flex items-center gap-2 font-sans-ui text-[11px] uppercase tracking-[0.3em] text-gold">
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
